import { HttpClient } from "@angular/common/http";
import { computed, DestroyRef, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SECURITY_SYSTEMS } from "../../pool-accounting/constants/security-system";
import { MESSAGE_TYPE } from "../../shared/constants/message-type";
import { OperationResult } from "../../shared/interfaces/operation-result";
import { LoadingService } from "../../shared/services/loading.service";
import { MessageService } from "../../shared/services/message.service";
import { UserInformation } from "../models/user-information";
import { UserPreferenceSecurityRoles } from "../models/user-preference-security-roles";
import { DbfEmailPreferences, LgipEmailPreferences } from "../models/user-email-preference";
import { map, of } from "rxjs";
import { SharedStateService } from "../../shared/services/shared-state.service";

type InvestementEmailPreferences = {
    emailLgipTransactionRequested?: boolean;
    emailLgipTransactionProcessed?: boolean;
    emailDbfTransactionRequested?: boolean;
    emailDbfTransactionProcessed?: boolean;
}

type BankingEmailPreferences = {
    receiveCountyReceiptHeader: boolean,
    receiveDepositHeader: boolean,
    receiveEftHeader: boolean,
    receiveMidHeader: boolean,
    receiveInterestProrationHeader: boolean,
    receiveReturnCheckHeader: boolean
}
@Injectable({
    providedIn: 'root'
})
export class AccountService {
    #httpClient = inject(HttpClient);
    #loadingService = inject(LoadingService);
    #messageService = inject(MessageService);
    #stateService = inject(SharedStateService);

    #api: string = 'user/';
    private destroyRef = inject(DestroyRef);

    private _userPreferenceAndSecurityRoles = signal(<UserPreferenceSecurityRoles> { userPreference: null, userSecurityRoles: [] });
    private _userInformation = signal(<UserInformation> {});
    private _rolesHaveLoaded = signal(false);
    private _bankingResetDisabled = signal(true);
    private _bankingValuesSubmitted = signal(true);
    private _investmentsResetDisabled = signal(true);
    private _investmentsValuesSubmitted = signal(true);

    readonly userPreferenceAndSecurityRoles = this._userPreferenceAndSecurityRoles.asReadonly();
    readonly userInformation = this._userInformation.asReadonly();
    readonly rolesHaveLoaded = this._rolesHaveLoaded.asReadonly();
    readonly bankingResetDisabled = this._bankingResetDisabled.asReadonly();
    readonly bankingValuesSubmitted = this._bankingValuesSubmitted.asReadonly();
    readonly investmentsResetDisabled = this._investmentsResetDisabled.asReadonly();
    readonly investmentsValuesSubmitted = this._investmentsValuesSubmitted.asReadonly();

    readonly userPreference = computed(() => this._userPreferenceAndSecurityRoles().userPreference);
    readonly userSecurityRoles = computed(() => this._userPreferenceAndSecurityRoles().userSecurityRoles);
    readonly hasBankingRole = computed(() => this.userPreferenceAndSecurityRoles().userSecurityRoles.some(x => x.securitySystemId === SECURITY_SYSTEMS.Banking));
    readonly hasLgipRole = computed(() => this.userPreferenceAndSecurityRoles().userSecurityRoles.some(x => x.securitySystemId === SECURITY_SYSTEMS.LGIP));
    readonly hasDbfRole = computed(() => this.userPreferenceAndSecurityRoles().userSecurityRoles.some(x => x.securitySystemId === SECURITY_SYSTEMS.DBF));

    setUserPreferenceSignal(contactInformation: UserInformation) {
        let userPreference = this.userPreferenceAndSecurityRoles().userPreference;
        const securityRoles = this.userPreferenceAndSecurityRoles().userSecurityRoles;
        userPreference = { ...userPreference, ...contactInformation };
        this._userPreferenceAndSecurityRoles.set({ userPreference: userPreference, userSecurityRoles: securityRoles });
    }

    toggleBankingResetDisabledSignal(disabled: boolean) {
        this._bankingResetDisabled.set(disabled);
    }

    toggleBankingValuesSubmittedSignal(disabled: boolean) {
        this._bankingValuesSubmitted.set(disabled);
    }

    toggleInvestmentsResetDisabledSignal(disabled: boolean) {
        this._investmentsResetDisabled.set(disabled);
    }

    toggleInvestmentsValuesSubmittedSignal(disabled: boolean) {
        this._investmentsValuesSubmitted.set(disabled);
    }

    getUserInformationById(userInformationId: number) {
        this.#loadingService.showLoadingIndicator();
        this.#httpClient.get<UserInformation>(`${this.#api}user-information-by-id/${userInformationId}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            this._userInformation.set(response);
            this.#loadingService.hideLoadingIndicator();
        });
    }

    getUserInformationByIdSubscribe(userInformationId: number) {
        return this.#httpClient.get<UserInformation>(`${this.#api}user-information-by-id/${userInformationId}`).pipe(map((response) => {
            return response;
        }));
    }

    setUserInformation(userInformation: UserInformation) {
        this._userInformation.set(userInformation);
    }

    getUserSecurityRoles() {
        this.#loadingService.showLoadingIndicator
        this.#httpClient.get<UserPreferenceSecurityRoles>(`${this.#api}user-preference-security-roles/3`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            this._userPreferenceAndSecurityRoles.set(response);
            this._rolesHaveLoaded.set(true);
            this.#loadingService.hideLoadingIndicator();
        });
    }

    removeUserWebsiteTracker() {
      if(this.#stateService.IsLoggedIn()) {
        return this.#httpClient.get<OperationResult>(`${this.#api}user-website-tracking`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            //console.log('User website tracker removed:', response);
            //return response as OperationResult;
        });
      }
      return of();
    }

    updateBankingEmailPreferences(bankingEmailPreferences: BankingEmailPreferences) {
        this.#loadingService.showLoadingIndicator
        return this.#httpClient.post<OperationResult>(`${this.#api}banking-email-preference`, bankingEmailPreferences).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            if (response.messageType === MESSAGE_TYPE.Success) {
                this.toggleBankingResetDisabledSignal(true);
                this.toggleBankingValuesSubmittedSignal(true);
                this.getUserSecurityRoles();
            }

            this.#loadingService.hideLoadingIndicator();
            this.#messageService.showSnackbarMessage({ message: response.message, messageType: MESSAGE_TYPE.Success });
        });
    }

    updateContactInformation(contactInformation: UserInformation) {
        this.#loadingService.showLoadingIndicator
        this.#httpClient.post<OperationResult>(`${this.#api}change-contact-information`, contactInformation).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            if (response.messageType === MESSAGE_TYPE.Success) {
                this.setUserPreferenceSignal(contactInformation);
            }
            this.#loadingService.hideLoadingIndicator();
            this.#messageService.showSnackbarMessage({ message: response.message, messageType: MESSAGE_TYPE.Success });

        });
    }

    updateDbfEmailPreferences(emailPreferences: DbfEmailPreferences) {
        this.#loadingService.showLoadingIndicator();
        return this.#httpClient.post<OperationResult>(`${this.#api}dbf-email-preference`, emailPreferences).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            if (response.messageType === MESSAGE_TYPE.Success) {
                this.toggleInvestmentsResetDisabledSignal(true);
                this.toggleInvestmentsValuesSubmittedSignal(true);
                this.getUserSecurityRoles();
            }

            this.#loadingService.hideLoadingIndicator();
            this.#messageService.showSnackbarMessage({ message: response.message, messageType: MESSAGE_TYPE.Success });
        });
    }

    updateInvestmentsEmailPreferences(emailPreferences: InvestementEmailPreferences) {
        this.#loadingService.showLoadingIndicator();
        return this.#httpClient.post<OperationResult>(`${this.#api}investments-email-preference`, emailPreferences).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            if (response.messageType === MESSAGE_TYPE.Success) {
                this.toggleInvestmentsResetDisabledSignal(true);
                this.toggleInvestmentsValuesSubmittedSignal(true);
                this.getUserSecurityRoles();
            }

            this.#loadingService.hideLoadingIndicator();
            this.#messageService.showSnackbarMessage({ message: response.message, messageType: MESSAGE_TYPE.Success });
        });
    }

    updateLgipEmailPreferences(emailPreferences: LgipEmailPreferences) {
        this.#loadingService.showLoadingIndicator();
        return this.#httpClient.post<OperationResult>(`${this.#api}lgip-email-preference`, emailPreferences).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
            if (response.messageType === MESSAGE_TYPE.Success) {
                this.toggleInvestmentsResetDisabledSignal(true);
                this.toggleInvestmentsValuesSubmittedSignal(true);
                this.getUserSecurityRoles();
            }

            this.#loadingService.hideLoadingIndicator();
            this.#messageService.showSnackbarMessage({ message: response.message, messageType: MESSAGE_TYPE.Success });
        });
    }

    forgotPassword(userName: string) {
        return this.#httpClient.post<OperationResult>(`${this.#api}forgot-password`, { UserName: userName });
    }

    forgotUsername(emailAddress: string) {
        return this.#httpClient.post<OperationResult>(`${this.#api}forgot-username`, { EmailAddress: emailAddress });
    }

    validateTemporaryCode(temporaryCode: string) {
        return this.#httpClient.post<OperationResult>(`${this.#api}validate-temporary-code`, { TemporaryCode: temporaryCode });
    }

    updatePassword(temporaryCode: string, password: string) {
        return this.#httpClient.post<OperationResult>(`${this.#api}update-password`, { TemporaryCode: temporaryCode, Password: password });
    }

    updatePasswordEncrypted(temporaryCode: string, password: string) {
        return this.#httpClient.post<OperationResult>(`${this.#api}update-password-encrypted`, { TemporaryCode: temporaryCode, Password: password });
    }

    getUserInformationByTemporaryCodeSubscribe(temporaryCode: string) {
        return this.#httpClient.post<UserInformation>(`${this.#api}user-information-by-temporary-code/`, { TemporaryCode: temporaryCode }).pipe(map((response) => {
            return response;
        }));
    }

    updateWebsiteTracker(): void {
      if(this.#stateService.IsLoggedIn()) {
        this.#httpClient.put(`${this.#api}user-website-tracking`, {})
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
      }
    }

}